import React, {useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  Save as SaveIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import {Field, Form, Formik, useFormikContext} from 'formik';
import * as Yup from 'yup';
import PageWithHeader from "../common/PageWithHeader";
import {useAppSelector} from "../../redux/hooks";
import {
  FeedbackSurveyData,
  useGetUsersQuery,
  usePostFeedbackSurveyMutation,
  usePutFeedbackSurveyDraftMutation,
  useLazyGetFeedbackSurveyDraftByUserIdAndReviewerIdReviewPeriodQuery,
  User,
} from "../../redux/vmsApi";
import {generateQuartersForYear} from "../../utils/periodUtil";
import { useRouter } from '../../hooks/useRouter';

interface SurveyValues {
  employee: string;
  reviewPeriod: string;
  responses: {
    [key: string]: number | string;
  };
}

// Rating questions that need 1-10 scale
const ratingQuestions = [
  'respectValues', 'decisionValues', 'encourageValues',
  'goodDecisions', 'highStandards', 'motivates',
  'controlEmotions', 'awareness', 'strongSkills', 'empathy', 'selfMotivated',
  'urgentWork', 'meetsDeadlines', 'exceedsExpectations',
  'fullUnderstanding', 'expertSkills',
  'gracefulDisagree', 'adaptsEasily', 'equalTreatment',
  'clearGoalsSelf', 'clearGoalsTeam', 'performanceReviews', 'manageUnderperformance'
];

const initialValues: SurveyValues = {
  employee: '',
  reviewPeriod: '',
  responses: Object.fromEntries(ratingQuestions.map(q => [q, 5])),
};

const validationSchema = Yup.object({
  employee: Yup.string().required('Employee is required'),
  reviewPeriod: Yup.string().required('Review Period is required'),
  responses: Yup.object().shape({
    ...Object.fromEntries(ratingQuestions.map(q => [q, Yup.number().min(1).max(10)])),
    improveValues: Yup.string().optional(),
    improveLeadership: Yup.string().optional(),
    improveEmotional: Yup.string().optional(),
    improveProductivity: Yup.string().optional(),
    improveJobKnowledge: Yup.string().optional(),
    improveInterpersonal: Yup.string().optional(),
    improvePerformance: Yup.string().optional(),
    strength: Yup.string().optional(),
    opportunity: Yup.string().optional(),
  }),
});

// Rating labels
const ratingLabels: { [key: number]: { label: string; color: string } } = {
  1: { label: 'Very Poor', color: '#d32f2f' },
  2: { label: 'Poor', color: '#f44336' },
  3: { label: 'Below Average', color: '#ff5722' },
  4: { label: 'Slightly Below Average', color: '#ff9800' },
  5: { label: 'Average', color: '#ffc107' },
  6: { label: 'Slightly Above Average', color: '#cddc39' },
  7: { label: 'Good', color: '#8bc34a' },
  8: { label: 'Very Good', color: '#4caf50' },
  9: { label: 'Excellent', color: '#2e7d32' },
  10: { label: 'Outstanding', color: '#1b5e20' },
};

// Section colors (blue theme variations)
const sectionColors: { [key: string]: string } = {
  'VALUES': '#1976d2',
  'LEADERSHIP SKILLS': '#1565c0',
  'EMOTIONAL INTELLIGENCE': '#0d47a1',
  'PRODUCTIVITY': '#1e88e5',
  'JOB KNOWLEDGE': '#2196f3',
  'INTERPERSONAL SKILLS': '#42a5f5',
  'PERFORMANCE MANAGEMENT': '#1976d2',
  'SUMMARY': '#0d47a1',
};

// Rating slider component
const RatingSlider: React.FC<{
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  disabled: boolean;
}> = ({ id, label, value, onChange, disabled }) => {
  const currentRating = ratingLabels[value] || ratingLabels[5];
  const marks = [
    { value: 1, label: '1' },
    { value: 5, label: '5' },
    { value: 10, label: '10' },
  ];
  
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, '&:hover': { backgroundColor: '#f5f5f5' } }}>
      <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ px: 1 }}>
        <Slider
          value={value || 5}
          min={1}
          max={10}
          step={1}
          marks={marks}
          valueLabelDisplay="auto"
          disabled={disabled}
          onChange={(_, newValue) => onChange(newValue as number)}
          sx={{
            '& .MuiSlider-thumb': {
              backgroundColor: currentRating.color,
              width: 20,
              height: 20,
            },
            '& .MuiSlider-track': {
              backgroundColor: currentRating.color,
            },
            '& .MuiSlider-rail': {
              backgroundColor: '#e0e0e0',
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Chip 
            label={`${value || 5} - ${currentRating.label}`} 
            size="small" 
            sx={{ backgroundColor: currentRating.color, color: 'white', fontWeight: 'bold' }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

// Collapsible Section Component
interface SectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  itemCount?: number;
  avgRating?: number;
}

const CollapsibleSection: React.FC<SectionProps> = ({ 
  title, 
  children, 
  defaultExpanded = true,
  itemCount,
  avgRating 
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const color = sectionColors[title] || '#757575';

  return (
    <Card sx={{ mb: 3, border: `2px solid ${color}` }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: color,
          color: 'white',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h6" sx={{ color: 'white' }}>{title}</Typography>
          {itemCount !== undefined && (
            <Chip
              label={`${itemCount} questions`}
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.9)', color: color }}
            />
          )}
          {avgRating !== undefined && avgRating > 0 && (
            <Chip
              label={`Avg: ${avgRating.toFixed(1)}`}
              size="small"
              sx={{ backgroundColor: ratingLabels[Math.round(avgRating)]?.color || '#757575', color: 'white' }}
            />
          )}
        </Stack>
        <IconButton sx={{ color: 'white' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      </Collapse>
    </Card>
  );
};

// Performance rating based on percentage (1-10 scale = 10%-100%)
const PERFORMANCE_RATINGS = [
  { grade: 'Outstanding', minPercent: 90, maxPercent: 100, color: '#1b5e20', description: 'Exceptional performance' },
  { grade: 'Excellent', minPercent: 80, maxPercent: 89.9, color: '#2e7d32', description: 'Exceeds expectations' },
  { grade: 'Very Good', minPercent: 70, maxPercent: 79.9, color: '#4caf50', description: 'Above expectations' },
  { grade: 'Good', minPercent: 60, maxPercent: 69.9, color: '#8bc34a', description: 'Meets expectations' },
  { grade: 'Average', minPercent: 50, maxPercent: 59.9, color: '#ffc107', description: 'Meets minimum expectations' },
  { grade: 'Below Average', minPercent: 40, maxPercent: 49.9, color: '#ff9800', description: 'Needs improvement' },
  { grade: 'Poor', minPercent: 20, maxPercent: 39.9, color: '#f44336', description: 'Significant improvement needed' },
  { grade: 'Very Poor', minPercent: 10, maxPercent: 19.9, color: '#d32f2f', description: 'Unacceptable performance' },
];

const getPerformanceRating = (percent: number) => {
  return PERFORMANCE_RATINGS.find(r => percent >= r.minPercent && percent <= r.maxPercent) || PERFORMANCE_RATINGS[PERFORMANCE_RATINGS.length - 1];
};

// Feedback Summary Component
interface FeedbackSummaryProps {
  sections: { title: string; avgRating: number }[];
  overallPercent: number;
  questionsAnswered: number;
  totalQuestions: number;
}

const FeedbackSummary: React.FC<FeedbackSummaryProps> = ({
  sections,
  overallPercent,
  questionsAnswered,
  totalQuestions,
}) => {
  const rating = getPerformanceRating(overallPercent);
  const completionPercentage = totalQuestions > 0 ? (questionsAnswered / totalQuestions) * 100 : 0;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Feedback Summary
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={3}>
        {/* Section Breakdown */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Section Scores
          </Typography>
          {sections.map((section, index) => {
            const sectionPercent = (section.avgRating / 10) * 100;
            const sectionColor = ratingLabels[Math.round(section.avgRating)]?.color || '#ffc107';
            return (
              <Box key={index} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{section.title}</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: sectionColor }}>
                    {sectionPercent.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={sectionPercent}
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: sectionColor,
                    }
                  }}
                />
              </Box>
            );
          })}
          
          {/* Completion Progress */}
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Completion</Typography>
              <Typography variant="body2" fontWeight="bold">
                {questionsAnswered} / {totalQuestions} questions
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: completionPercentage === 100 ? '#4caf50' : '#1976d2',
                }
              }}
            />
          </Box>
        </Grid>

        {/* Overall Score */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              textAlign: 'center',
              p: 3,
              borderRadius: 2,
              backgroundColor: rating.color,
              color: 'white',
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Overall Rating
            </Typography>
            <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
              {overallPercent.toFixed(0)}%
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold' }}>
              {rating.grade}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
              {rating.description}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Rating Scale Reference */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Performance Rating Scale
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {[...PERFORMANCE_RATINGS].reverse().map((r) => (
            <Tooltip
              key={r.grade}
              title={`${r.minPercent}% - ${r.maxPercent === 100 ? '100' : r.maxPercent.toFixed(0)}% | ${r.description}`}
            >
              <Chip
                label={r.grade}
                size="small"
                sx={{
                  backgroundColor: r.color,
                  color: 'white',
                  fontWeight: rating.grade === r.grade ? 'bold' : 'normal',
                  border: rating.grade === r.grade ? '2px solid black' : 'none',
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

const SurveyForm: React.FC<{ disabled: boolean; onFieldChange?: () => void }> = ({disabled, onFieldChange}) => {
  const {values, setFieldValue} = useFormikContext<SurveyValues>();
  const {data: users} = useGetUsersQuery();

  const selectedUser = users?.find(user => user.id === values.employee);
  const pronounOne = selectedUser?.gender === 'male' ? 'he' : 'she';
  const pronounTwo = selectedUser?.gender === 'male' ? 'his' : 'her';
  const pronounThree = selectedUser?.gender === 'male' ? 'himself' : 'herself';
  const name = selectedUser?.name || '[Name]';

  const handleRatingChange = (questionId: string, value: number) => {
    setFieldValue(`responses.${questionId}`, value);
    onFieldChange?.();
  };
  
  const handleTextChange = (questionId: string, value: string) => {
    setFieldValue(`responses.${questionId}`, value);
    onFieldChange?.();
  };

  // Calculate section averages (use default of 5 for all questions to start at 50%)
  const getSectionAverage = (questionIds: string[]) => {
    // Always calculate average using 5 as default for unanswered questions
    const ratings = questionIds.map(id => (values.responses[id] as number) ?? 5);
    return ratings.reduce((sum, r) => sum + r, 0) / ratings.length;
  };

  const sections = [
    {
      title: 'VALUES',
      items: [
        {id: 'respectValues', label: `Respect the WestProp Values evident in ${name}'s conduct`},
        {id: 'decisionValues', label: `Makes decisions in line with WestProp values`},
        {id: 'encourageValues', label: `Encourages team members to live the WestProp values`},
      ],
      textField: {
        id: 'improveValues',
        label: `What suggestions do you have to help ${name} improve ${pronounTwo} living of the WestProp values`
      }
    },
    {
      title: 'LEADERSHIP SKILLS',
      items: [
        {id: 'goodDecisions', label: `Makes good decisions and trusts the team when delegating tasks`},
        {id: 'highStandards', label: `The team is working to a high standard and cohesively towards the high-level goals and objectives of the department`},
        {id: 'motivates', label: `Motivates others to reach their goals`},
      ],
      textField: {
        id: 'improveLeadership',
        label: `What suggestions do you have to help ${name} improve ${pronounTwo} leadership skills`
      }
    },
    {
      title: 'EMOTIONAL INTELLIGENCE',
      items: [
        {id: 'controlEmotions', label: `Controls ${pronounTwo} emotions even in high pressure situations`},
        {id: 'awareness', label: `Demonstrates an awareness of how ${pronounTwo} actions and decisions affect others`},
        {id: 'strongSkills', label: `Has strong skills and is able to interact well with others`},
        {id: 'empathy', label: `Demonstrates empathy and is able to recognize and respond appropriately to the emotional state of others`},
        {id: 'selfMotivated', label: `Is self motivated, striving to achieve ${pronounTwo} goal and always looking for ways to do better`},
      ],
      textField: {
        id: 'improveEmotional',
        label: `What suggestions do you have to help ${name} improve ${pronounTwo} emotional intelligence`
      }
    },
    {
      title: 'PRODUCTIVITY',
      items: [
        {id: 'urgentWork', label: `Delivers urgent work without compromising on the details`},
        {id: 'meetsDeadlines', label: `Meets deadlines and makes the best time use`},
        {id: 'exceedsExpectations', label: `Exceeds expectations by delivering more than assigned work despite the tight schedule`},
      ],
      textField: {
        id: 'improveProductivity',
        label: `What suggestions do you have to help ${name} improve ${pronounTwo} productivity`
      }
    },
    {
      title: 'JOB KNOWLEDGE',
      items: [
        {id: 'fullUnderstanding', label: `Has full understanding of ${pronounTwo} role and responsibility in ${pronounTwo} department`},
        {id: 'expertSkills', label: `Is an expert and performs job responsibilities skillfully`},
      ],
      textField: {
        id: 'improveJobKnowledge',
        label: `What suggestions do you have to help ${name} improve ${pronounTwo} job knowledge`
      }
    },
    {
      title: 'INTERPERSONAL SKILLS',
      items: [
        {id: 'gracefulDisagree', label: `Even when disagreeing with others, ${name} does it gracefully and respectfully`},
        {id: 'adaptsEasily', label: `Adapts easily to various situations and different people`},
        {id: 'equalTreatment', label: `Equal in ${pronounTwo} treatment of everyone and communicates with ${pronounTwo} peers respectfully`},
      ],
      textField: {
        id: 'improveInterpersonal',
        label: `What suggestions do you have to help ${name} improve ${pronounTwo} interpersonal skills`
      }
    },
    {
      title: 'PERFORMANCE MANAGEMENT',
      items: [
        {id: 'clearGoalsSelf', label: `Sets clear goals, objectives and targets for ${pronounThree}`},
        {id: 'clearGoalsTeam', label: `Sets and communicates clear goals, objectives and targets for ${pronounTwo} team`},
        {id: 'performanceReviews', label: `Conducts performance review meetings/appraisals with ${pronounTwo} team timorously and objectively`},
        {id: 'manageUnderperformance', label: `Is proactive in managing underperformance of ${pronounTwo} team by initiating and monitoring appropriate interventions e.g. training`},
      ],
      textField: {
        id: 'improvePerformance',
        label: `What suggestions do you have to help ${name} improve ${pronounTwo} performance management and execution`
      }
    },
  ];

  // Calculate summary data
  const sectionSummaries = sections.map(section => ({
    title: section.title,
    avgRating: getSectionAverage(section.items.map(i => i.id)),
  }));

  const allQuestionIds = sections.flatMap(s => s.items.map(i => i.id));
  const questionsAnswered = allQuestionIds.filter(id => values.responses[id] !== undefined).length;
  const totalQuestions = allQuestionIds.length;
  
  // Calculate overall average (always has a value since getSectionAverage uses default of 5)
  const overallAverage = sectionSummaries.reduce((sum, s) => sum + s.avgRating, 0) / sectionSummaries.length;
  const overallPercent = (overallAverage / 10) * 100;

  return (
    <Stack spacing={0} width="100%">
      {/* Feedback Summary */}
      <FeedbackSummary
        sections={sectionSummaries}
        overallPercent={overallPercent}
        questionsAnswered={questionsAnswered}
        totalQuestions={totalQuestions}
      />
      
      {sections.map((section, sIndex) => (
        <CollapsibleSection
          key={sIndex}
          title={section.title}
          itemCount={section.items.length}
          avgRating={getSectionAverage(section.items.map(i => i.id))}
        >
          <Stack spacing={2}>
            {section.items.map((item, index) => (
              <RatingSlider
                key={index}
                id={item.id}
                label={item.label}
                value={(values.responses[item.id] as number) || 5}
                onChange={(value) => handleRatingChange(item.id, value)}
                disabled={disabled}
              />
            ))}
            <TextField
              fullWidth
              multiline
              rows={3}
              label={section.textField.label}
              value={values.responses[section.textField.id] || ''}
              onChange={(e) => handleTextChange(section.textField.id, e.target.value)}
              disabled={disabled}
              variant="outlined"
              sx={{ mt: 2 }}
            />
          </Stack>
        </CollapsibleSection>
      ))}
      
      {/* Summary Section */}
      <CollapsibleSection title="SUMMARY" itemCount={2}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label={`What is ${name}'s greatest strength and what should ${pronounOne} continue to do to grow and develop?`}
            value={values.responses['strength'] || ''}
            onChange={(e) => handleTextChange('strength', e.target.value)}
            disabled={disabled}
            variant="outlined"
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label={`What is ${name}'s greatest opportunity and what can ${pronounOne} do to improve ${pronounTwo} performance?`}
            value={values.responses['opportunity'] || ''}
            onChange={(e) => handleTextChange('opportunity', e.target.value)}
            disabled={disabled}
            variant="outlined"
          />
        </Stack>
      </CollapsibleSection>
    </Stack>
  );
};

// Auto-save hook for debounced draft saving
const useAutoSave = (
  values: SurveyValues,
  currentUserId: string,
  saveDraft: (data: FeedbackSurveyData) => Promise<unknown>,
  hasChanges: boolean,
  setLastSavedAt: (date: Date | null) => void,
  setIsSaving: (saving: boolean) => void,
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Check conditions: employee selected, review period selected, and changes made
    const canSave = values.employee && values.reviewPeriod && hasChanges;
    
    if (!canSave) return;
    
    // Debounce auto-save by 2 seconds
    timeoutRef.current = setTimeout(async () => {
      const dataToSave: FeedbackSurveyData = {
        userId: values.employee,
        reviewerId: currentUserId,
        reviewPeriod: values.reviewPeriod,
        status: 'draft',
        responses: Object.keys(values.responses).map(key => {
          const response = values.responses[key];
          return {
            questionId: key,
            responseRating: typeof response === 'number' ? response : undefined,
            responseText: typeof response === 'string' ? response : undefined,
          };
        }),
      };
      
      try {
        setIsSaving(true);
        await saveDraft(dataToSave);
        setLastSavedAt(new Date());
      } catch (error) {
        console.error('Auto-save failed:', error);
      } finally {
        setIsSaving(false);
      }
    }, 2000);
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [values, currentUserId, saveDraft, hasChanges, setLastSavedAt, setIsSaving]);
};

const SurveyFormPage: React.FC = () => {
  const router = useRouter();
  const {data: users} = useGetUsersQuery();
  const [postFeedbackSurvey] = usePostFeedbackSurveyMutation();
  const [putFeedbackSurveyDraft] = usePutFeedbackSurveyDraftMutation();
  const [getDraft] = useLazyGetFeedbackSurveyDraftByUserIdAndReviewerIdReviewPeriodQuery();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [loadedDraft, setLoadedDraft] = useState<SurveyValues | null>(null);
  const isLoadingDraftRef = useRef(false);

  const currentUser = useAppSelector(state => state.auth.user);
  // Generate all quarters for 2025 and 2026 - users can provide feedback for any quarter
  const pastQuarters = [...generateQuartersForYear(2025), ...generateQuartersForYear(2026)];

  const handleSubmit = async (values: SurveyValues, {resetForm}: { resetForm: () => void }) => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    const dataToSubmit: FeedbackSurveyData = {
      userId: values.employee,
      reviewerId: currentUser?.id ?? '',
      reviewPeriod: values.reviewPeriod,
      responses: Object.keys(values.responses).map(key => {
        const response = values.responses[key];
        return {
          questionId: key,
          responseRating: typeof response === 'number' ? response : undefined,
          responseText: typeof response === 'string' ? response : undefined,
        };
      }),
    };

    try {
      await postFeedbackSurvey({feedbackSurveyData: dataToSubmit}).unwrap();
      setIsSubmitted(true);
      resetForm();
    } catch (error) {
      console.error('There was an error submitting the form', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to load draft when employee and period changes
  const loadDraft = useCallback(async (employeeId: string, reviewPeriod: string, setFieldValue: (field: string, value: unknown) => void) => {
    if (!employeeId || !reviewPeriod || !currentUser?.id || isLoadingDraftRef.current) return;
    
    isLoadingDraftRef.current = true;
    try {
      const result = await getDraft({
        userId: employeeId,
        reviewerId: currentUser.id,
        reviewPeriod: reviewPeriod,
      }).unwrap();
      
      if (result) {
        // Load draft data into form
        const responsesObj: { [key: string]: number | string } = {};
        result.responses.forEach(r => {
          if (r.responseRating !== undefined) {
            responsesObj[r.questionId] = r.responseRating;
          } else if (r.responseText !== undefined) {
            responsesObj[r.questionId] = r.responseText;
          }
        });
        
        // Merge with default values for any missing rating questions
        ratingQuestions.forEach(q => {
          if (responsesObj[q] === undefined) {
            responsesObj[q] = 5;
          }
        });
        
        setFieldValue('responses', responsesObj);
        if (result.lastSavedAt) {
          setLastSavedAt(new Date(result.lastSavedAt));
        }
        setHasChanges(false); // Reset changes flag after loading
      }
    } catch (error) {
      // No draft found, that's okay
      console.log('No existing draft found');
    } finally {
      isLoadingDraftRef.current = false;
    }
  }, [currentUser?.id, getDraft]);

  const saveDraft = useCallback(async (data: FeedbackSurveyData) => {
    return putFeedbackSurveyDraft({ feedbackSurveyDraftData: data }).unwrap();
  }, [putFeedbackSurveyDraft]);

  // Format last saved time
  const formatLastSaved = (date: Date | null) => {
    if (!date) return null;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 60) return 'Just now';
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    return date.toLocaleTimeString();
  };

  return (
    <PageWithHeader>
      {!isSubmitted ? (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({values, setFieldValue}) => {
            const formDisabled = !values.employee || !values.reviewPeriod || isSubmitting;
            const selectedUser = users?.find(u => u.id === values.employee);
            
            // Effect to load draft when employee or period changes
            useEffect(() => {
              if (values.employee && values.reviewPeriod) {
                loadDraft(values.employee, values.reviewPeriod, setFieldValue);
              }
            }, [values.employee, values.reviewPeriod]);
            
            // Effect to track changes
            const handleFieldChange = (field: string, value: unknown) => {
              setFieldValue(field, value);
              setHasChanges(true);
            };
            
            // Auto-save effect
            useAutoSave(
              values,
              currentUser?.id ?? '',
              saveDraft,
              hasChanges,
              setLastSavedAt,
              setIsSaving,
            );
            
            return (
              <Form>
                <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton onClick={() => router.push('/reviews')}>
                        <ArrowBackIcon />
                      </IconButton>
                      <Typography variant="h4">360° Feedback Survey</Typography>
                      {/* Auto-save status */}
                      {values.employee && values.reviewPeriod && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                          {isSaving ? (
                            <Chip
                              icon={<SaveIcon sx={{ fontSize: 16 }} />}
                              label="Saving..."
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : lastSavedAt ? (
                            <Tooltip title={`Last saved: ${lastSavedAt.toLocaleString()}`}>
                              <Chip
                                icon={<SaveIcon sx={{ fontSize: 16 }} />}
                                label={`Draft saved ${formatLastSaved(lastSavedAt)}`}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : null}
                        </Box>
                      )}
                    </Box>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="outlined"
                        startIcon={<ListAltIcon />}
                        onClick={() => router.push('/reviews/360-degree-survey/my-submissions')}
                      >
                        My Surveys
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => router.push('/reviews/360-degree-survey/feedback')}
                      >
                        View Feedback
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={async () => {
                          if (!values.employee || !values.reviewPeriod) return;
                          setIsSaving(true);
                          try {
                            const dataToSave: FeedbackSurveyData = {
                              userId: values.employee,
                              reviewerId: currentUser?.id ?? '',
                              reviewPeriod: values.reviewPeriod,
                              status: 'draft',
                              responses: Object.keys(values.responses).map(key => {
                                const response = values.responses[key];
                                return {
                                  questionId: key,
                                  responseRating: typeof response === 'number' ? response : undefined,
                                  responseText: typeof response === 'string' ? response : undefined,
                                };
                              }),
                            };
                            await saveDraft(dataToSave);
                            setLastSavedAt(new Date());
                          } catch (error) {
                            console.error('Save draft failed:', error);
                          } finally {
                            setIsSaving(false);
                          }
                        }}
                        disabled={!values.employee || !values.reviewPeriod || isSaving}
                      >
                        {isSaving ? 'Saving...' : 'Save Draft'}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        type="submit"
                        disabled={formDisabled}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                      </Button>
                    </Stack>
                  </Box>

                  {/* Employee & Period Selection */}
                  <Paper sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Employee *</InputLabel>
                          <Field
                            name="employee"
                            as={Select}
                            label="Employee *"
                            onChange={(event: SelectChangeEvent) => {
                              handleFieldChange('employee', event.target.value);
                            }}
                          >
                            {users && users.map((user: User) => (
                              <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Review Period *</InputLabel>
                          <Field
                            name="reviewPeriod"
                            as={Select}
                            label="Review Period *"
                            onChange={(event: SelectChangeEvent) => {
                              handleFieldChange('reviewPeriod', event.target.value);
                            }}
                          >
                            {pastQuarters.map((quarter, index) => (
                              <MenuItem key={index} value={quarter}>{quarter}</MenuItem>
                            ))}
                          </Field>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          {selectedUser 
                            ? `Providing feedback for ${selectedUser.name}. Rate each statement on a scale of 1-10.`
                            : 'Select an employee and review period to begin the survey.'
                          }
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Survey Form */}
                  <SurveyForm disabled={formDisabled} onFieldChange={() => setHasChanges(true)} />
                </Box>
              </Form>
            );
          }}
        </Formik>
      ) : (
        <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant='h4' gutterBottom sx={{ color: '#388e3c' }}>
              Submission Successful!
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Thank you for providing your feedback. Your responses have been recorded.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                onClick={() => router.push('/reviews')}
              >
                Back to Dashboard
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setIsSubmitted(false)}
              >
                Submit Another
              </Button>
            </Stack>
          </Paper>
        </Box>
      )}
    </PageWithHeader>
  );
};

export default SurveyFormPage;
